import { useEffect, useState, useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { isEmpty, map, pick } from 'lodash';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import Modal from '../../../sections/modal/Modal';
import DraggableList from '../drag-drop/DraggableList';

const SynonymSetModal = ({
  open,
  handleClose,
  handleCreate,
  handleEdit,
  set,
}) => {
  const { t } = useTranslation();
  const seqRef = useRef(0);

  const [wordList, setWordList] = useState([]);
  const [submitError, setSubmitError] = useState('');

  const newWordSchema = object({
    newWord: string()
      .trim()
      .test('duplicate', '', function (value) {
        const { path, createError } = this;
        if (!value) return true;
        if (map(wordList, 'value').includes(value)) {
          return createError({
            path,
            message: t('duplicateValueAdded', {
              ns: 'validations',
              value: value,
            }),
          });
        }
        return true;
      }),
  });

  const schema = object({
    comment: string().max(
      500,
      t('maxLength', { ns: 'validations', max: '500' })
    ),
  });

  const {
    control: inFormControl,
    reset: inFormReset,
    setValue,
    watch,
    trigger,
    formState: { errors: inFormErrors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(newWordSchema),
    shouldUnregister: true,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(schema),
    shouldUnregister: true,
  });

  const newWord = watch('newWord');

  const addWord = () => {
    trigger('newWord').then((isValid) => {
      if (isValid) {
        setWordList((prevList) => [
          ...prevList,
          { id: `temp-${seqRef.current}`, value: newWord, notSaved: true },
        ]);
        seqRef.current++;
        setValue('newWord', '');
      }
    });
  };

  const closeModal = () => {
    setSubmitError('');
    handleClose();
  };

  const discardUnsavedChanges = () => {
    inFormReset({ newWord: '' });
    setSubmitError('');
    if (set) {
      // Edit
      setWordList(set.items);
      reset({ comment: set.comment || '' });
    } else {
      // Create
      setWordList([]);
      reset({ comment: '' });
    }
  };

  const onSubmit = async (data) => {
    const cleanedList = map(wordList, (item) => pick(item, ['value']));
    const payload = { ...data, items: cleanedList };
    const error = set
      ? await handleEdit(set.id, payload)
      : await handleCreate(payload);

    if (error) {
      setSubmitError(error);
    } else {
      setWordList([]);
      closeModal();
    }
  };

  useEffect(() => {
    if (set) {
      setWordList(set.items);
    }
  }, [open]);

  const title = set
    ? t('synonym.editSynonymSet', { ns: 'sectionTitles' })
    : t('synonym.createSynonymSet', { ns: 'sectionTitles' });

  return (
    <Modal openModal={open} title={title} handleClose={closeModal}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {submitError && (
            <Grid size={12}>
              <Alert severity="error">{submitError}</Alert>
            </Grid>
          )}
          <Grid size={12}>
            <Controller
              name="comment"
              control={control}
              defaultValue={set?.comment || ''}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  autoComplete="off"
                  error={!!errors?.comment}
                  helperText={errors?.comment?.message}
                  label={t('synonym.comment', { ns: 'fields' })}
                  inputProps={{ 'aria-label': 'comment' }}
                  minRows={2}
                  multiline
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name="newWord"
              control={inFormControl}
              defaultValue={''}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  autoComplete="off"
                  label={t('synonym.newWord', { ns: 'fields' })}
                  variant="outlined"
                  error={!!inFormErrors?.newWord}
                  helperText={inFormErrors?.newWord?.message}
                  inputProps={{
                    'aria-label': 'new word',
                  }}
                  style={{ marginBottom: 10 }}
                  InputProps={{
                    endAdornment: (
                      <Tooltip
                        title={t('synonym.addToList', { ns: 'project' })}
                        placement="top"
                        arrow={true}
                        open={!!newWord}
                      >
                        <span>
                          <IconButton
                            title={t('synonym.addWord', { ns: 'project' })}
                            aria-label="add word"
                            type="button"
                            edge="end"
                            style={{ marginRight: -8 }}
                            disabled={!newWord}
                            onClick={addWord}
                            size="large"
                          >
                            <AddIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ),
                  }}
                />
              )}
            />

            <Paper variant="outlined">
              {isEmpty(wordList) ? (
                <Box sx={{ m: 1 }}>
                  {t('synonym.atLeastOneWord', { ns: 'project' })}
                </Box>
              ) : (
                <DraggableList list={wordList} setList={setWordList} />
              )}
            </Paper>
          </Grid>
        </Grid>

        <DialogActions>
          <Button
            variant="text"
            type="button"
            onClick={discardUnsavedChanges}
            aria-label="discard"
            style={{ color: red[500] }}
          >
            {set
              ? t('discard', { ns: 'buttons' })
              : t('reset', { ns: 'buttons' })}
          </Button>
          <Button variant="text" type="reset" onClick={closeModal}>
            {t('cancel', { ns: 'buttons' })}
          </Button>

          <Button
            variant="text"
            type="submit"
            aria-label="confirm"
            disabled={!isValid || isEmpty(wordList)}
          >
            {t('confirm', { ns: 'buttons' })}
          </Button>
        </DialogActions>
      </form>
    </Modal>
  );
};

SynonymSetModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleCreate: PropTypes.func,
  handleEdit: PropTypes.func,
  set: PropTypes.shape({
    id: PropTypes.string.isRequired,
    comment: PropTypes.string,
    items: PropTypes.array,
  }),
};

export default SynonymSetModal;
